import React, { useState } from 'react';
import { alpha, Box, Divider, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { Check } from '@mui/icons-material';
import type { ChangesAgencyFilterCatalog } from '../../types/changesAgencyFilter';
import { AgencyFilterGroupedMenuList, type AgencyFilterTab } from './AgencyFilterGroupedMenuList';

type Props = {
  catalog: ChangesAgencyFilterCatalog;
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Chamado após selecionar «Todos» (lista vazia). Ex.: fechar menu no filtro da tabela. */
  onSelectAll?: () => void;
  maxHeight?: number;
};

function catalogHasAgencies(catalog: ChangesAgencyFilterCatalog): boolean {
  return (
    (catalog.federacao?.length ?? 0) > 0 ||
    (catalog.estados ?? []).some((g) => (g?.names?.length ?? 0) > 0) ||
    (catalog.municipios ?? []).some((s) =>
      (s?.cities ?? []).some((c) => (c?.names?.length ?? 0) > 0),
    )
  );
}

export function AgencyFilterPanel({
  catalog,
  selected,
  onChange,
  onSelectAll,
  maxHeight = 300,
}: Props) {
  const theme = useTheme();
  const [tab, setTab] = useState<AgencyFilterTab>('federacao');
  const allSelected = selected.length === 0;

  const handleToggle = (name: string) => {
    const next = selected.includes(name)
      ? selected.filter((n) => n !== name)
      : [...selected, name];
    onChange(next);
  };

  const handleSelectAll = () => {
    onChange([]);
    onSelectAll?.();
  };

  if (!catalogHasAgencies(catalog)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1.5 }}>
        Nenhuma agência disponível no catálogo.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ px: 1.5, pt: 1, pb: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minWidth: 0, px: 1, fontSize: '0.8125rem', textTransform: 'none' },
          }}
        >
          <Tab value="federacao" label="Federal" />
          <Tab value="estados" label="Estados" />
          <Tab value="municipios" label="Municípios" />
        </Tabs>
      </Box>
      <Box sx={{ maxHeight, overflowY: 'auto' }}>
        <Box
          component="button"
          type="button"
          onClick={handleSelectAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            textAlign: 'left',
            border: 'none',
            cursor: 'pointer',
            py: 1,
            px: 1.5,
            bgcolor: allSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.08) },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: 0.5,
              mr: 1.5,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: allSelected ? theme.palette.primary.main : 'transparent',
            }}
          >
            {allSelected && <Check sx={{ fontSize: 16, color: 'white' }} />}
          </Box>
          <Typography variant="body2" fontWeight={allSelected ? 600 : 400}>
            Todos
          </Typography>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        <AgencyFilterGroupedMenuList
          tab={tab}
          catalog={catalog}
          selected={selected}
          onToggle={handleToggle}
        />
      </Box>
    </Box>
  );
}
